/**
 * Week-ahead weather, reduced to what the grass-ID result screen can honestly
 * say about it.
 *
 * Deliberately separate from `mobile/lib/weather-adapter.ts` rather than shared:
 * that module carries a second, legacy payload shape for older app builds, and
 * editing anything under `mobile/` forces an App Store build. The THRESHOLDS
 * below are copied from it on purpose so both platforms tell the same story
 * about the same week — change them in both places or not at all.
 */

import type { WeatherData } from '@/lib/weather-utils'
import { GERMINATION_ROWS, type GerminationRow } from '@/data/soil-germination-temps'

/** A day counts as rain at either a confident chance or a measurable amount. */
const RAIN_CHANCE_PCT = 55
const RAIN_AMOUNT_IN = 0.08
/** Heat stress for turf, and the point where mowing height should come up. */
const HOT_HIGH_F = 90
/** Frost risk: holds seed and fertilizer. */
const FROST_LOW_F = 35

/**
 * Soil-temperature decision windows. Soil takes the lead only while the
 * reading sits INSIDE a window for a task the month actually carries; outside
 * one it is a footer token and the weather cascade runs unchanged.
 *
 * Pre-emergent: the crabgrass window every regional profile in
 * data/regional-climate.ts and every monthly task list states as 50-55°F at
 * 2 inches. Below 45 the number is trivia; above 58 the window has passed and
 * naming it would only tell the user they are late.
 */
const PRE_EMERGENT_SOIL_MIN_F = 45
const PRE_EMERGENT_SOIL_GO_F = 50
const PRE_EMERGENT_SOIL_MAX_F = 58
/** Seeding leads within this many degrees either side of the germination minimum. */
const SEEDING_SOIL_BAND_F = 10

/**
 * Bar heights scale against a FIXED per-day ceiling, never the week's own max.
 * Scaled to the visible maximum, a week of drizzle draws the same silhouette as
 * a week of storms, which makes the chart overstate exactly where trust matters.
 * Half an inch in a day is a genuinely wet day; anything above clamps full.
 */
export const PRECIP_CEILING_IN = 0.5

/**
 * Below this, a day is drawn as dry. Open-Meteo returns values like 0.004",
 * which round to "0.00 inches" in the label but still scale to a visible blue
 * sliver — a bar contradicting its own number.
 */
const TRACE_PRECIP_IN = 0.01
/**
 * Once a day does draw, give it a floor. A true 0.02" day scales to 4% of the
 * track, which is thinner than the dry-day tick and reads as no rain at all.
 * 12% of the 34px track is ~4px — visibly taller than the 2px dry tick, which
 * 6% was not (it rounded to the same 2px and made the floor useless on mobile).
 */
const MIN_VISIBLE_BAR_PCT = 12

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const DAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const DAY_INITIAL = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

export interface WeatherWeekDay {
  date: string
  /** Column header at narrow widths — one glyph fits ~37px. */
  initial: string
  /** Column header once there's room. Index 0 reads "Today". */
  shortLabel: string
  longLabel: string
  highF: number | null
  lowF: number | null
  precipIn: number
  /** 0-100 chance. Retained because a day can be a rain day on chance alone,
   *  with no forecast amount and therefore no bar to see. */
  precipChance: number
  /** 0-100, the share of `PRECIP_CEILING_IN` this day's rainfall fills. */
  barPct: number
  isToday: boolean
  isRain: boolean
  isHot: boolean
  isFrost: boolean
}

export type WeatherWeekKind = 'frost' | 'soil' | 'heat' | 'rain' | 'dry'

export interface WeatherWeekLead {
  kind: WeatherWeekKind
  /** The fact, stated plainly. Always safe to render. */
  headline: string
  /**
   * What the plan does about it. Null whenever the month's task list contains
   * no task that backs the claim — we never tell a user their watering moved
   * when their August plan has no watering task in it.
   */
  action: string | null
  /**
   * Which side of the line a soil verdict lands on. The colour family says
   * "soil"; this is what lets the icon say go or wait. Weather kinds omit it.
   */
  stance?: 'go' | 'hold'
}

export interface WeatherWeek {
  days: WeatherWeekDay[]
  totalPrecipIn: number
  rainDays: number
  peakHighF: number | null
  peakHighLabel: string | null
  /**
   * Seven-day mean soil temperature at 2 inches. Null when the payload has no
   * reading OR when the caller did not opt into soil, so a control-arm week
   * carries nothing for the footer to render.
   */
  soilTempF: number | null
  lead: WeatherWeekLead
}

/** What the caller knows about the lawn, beyond the forecast. */
export interface WeatherWeekContext {
  /** Show soil temperature (lead or footer). Off = today's four-kind cascade. */
  soil?: boolean
  /** The identified grass, e.g. "Bermudagrass". Needed for the seeding lead. */
  grassType?: string
  /**
   * Whether the grass name is settled (high-confidence ID or a user pick).
   * The seeding verdict names the species and its threshold as fact, so it
   * stays silent for a "best guess" result; the pre-emergent verdict is
   * grass-agnostic and does not need this.
   */
  grassConfirmed?: boolean
}

/** Which claims the month's tasks can support. Keyword-matched: `MonthlyTask`
 *  carries no category, only prose. */
export interface PlanTaskCapabilities {
  watering: boolean
  mowing: boolean
  /** Seeding and feeding are separate: a month may overseed without feeding. */
  seeding: boolean
  feeding: boolean
  preEmergent: boolean
}

const TASK_PATTERNS = {
  watering: /\bwater(ing|ed)?\b|\birrigat|\bsprinkler/,
  // Bare "height"/"cut" matched "Measure turf height" and similar; require the
  // mowing verb or an explicit mowing-height phrase.
  mowing: /\bmow(ing|er|ers)?\b|\btrim(ming)?\b|\b(mowing|cutting|deck) height\b/,
  seeding: /\bseed(ing|s)?\b|\boverseed/,
  feeding: /\bfertiliz|\bfeed(ing)?\b|\blawn food\b|\bnitrogen\b|\bpotassium\b/,
  preEmergent: /\bpre-?\s?emergent/,
} as const

/**
 * A negation that governs the matched verb. `data/guide-monthly-tasks.ts`
 * really does ship "Do not water, fertilize, or mow" (5 rows) and "Avoid
 * supplemental watering this month" — matching on the keyword alone would tell
 * those users to water on Friday, which is the exact failure this whole gate
 * exists to prevent.
 */
const NEGATION = /\b(avoid|do not|don'?t|never|skip|hold off|stop|no longer)\b/

/**
 * True only when the task authorizes the action. A negation counts when it sits
 * BEFORE the keyword, which is what distinguishes "Do not water…" (rejected)
 * from "Water early morning only, avoid evening watering" (allowed — a real
 * watering task that merely qualifies itself). Ambiguity resolves to false:
 * suppressing a true action costs a line of copy, asserting a false one costs
 * trust.
 */
function taskAuthorizes(task: string, pattern: RegExp): boolean {
  const lower = task.toLowerCase()
  const match = lower.match(pattern)
  if (!match || match.index === undefined) return false
  const negation = lower.match(NEGATION)
  if (negation && negation.index !== undefined && negation.index < match.index) return false
  return true
}

export function planTaskCapabilities(taskTexts: string[]): PlanTaskCapabilities {
  const authorizes = (pattern: RegExp) => taskTexts.some((task) => taskAuthorizes(task, pattern))
  return {
    watering: authorizes(TASK_PATTERNS.watering),
    mowing: authorizes(TASK_PATTERNS.mowing),
    seeding: authorizes(TASK_PATTERNS.seeding),
    feeding: authorizes(TASK_PATTERNS.feeding),
    preEmergent: authorizes(TASK_PATTERNS.preEmergent),
  }
}

/**
 * Lower-case name the action line can use mid-sentence: "bermuda seeding
 * holds". Kept OUT of the 13.5px headline: at the card's 286px the headline
 * has room for ~22 characters before it wraps and the box outgrows its
 * reserved height.
 * Keyed on `GerminationRow.grass`. "Bluegrass" and "ryegrass" keep the suffix
 * because stripping it leaves a colour and a cereal.
 */
const SEED_SHORT_NAMES: Record<string, string> = {
  'Annual Ryegrass': 'annual rye',
  'Perennial Ryegrass': 'ryegrass',
  'Tall Fescue': 'tall fescue',
  'Fine Fescue': 'fine fescue',
  'Kentucky Bluegrass': 'bluegrass',
  Buffalograss: 'buffalograss',
  Bermudagrass: 'bermuda',
  Bahia: 'bahia',
  Centipede: 'centipede',
  Zoysia: 'zoysia',
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function letters(value: string): string {
  return value.toLowerCase().replace(/[^a-z]/g, '')
}

/**
 * Match the identified grass to its germination row. Model output and plan
 * keys vary ("Bermuda", "Bermudagrass", "St. Augustine", "St. Augustinegrass"),
 * so compare on letters only and accept a prefix either way. Rows that carry a
 * note are sod/plug grasses with no real seeding decision and return null.
 */
export function germinationRowFor(grassType: string | undefined): GerminationRow | null {
  if (!grassType) return null
  const key = letters(grassType)
  if (!key) return null
  const row = GERMINATION_ROWS.find((r) => {
    const rowKey = letters(r.grass)
    return key === rowKey || key.startsWith(rowKey) || rowKey.startsWith(key)
  })
  if (!row || row.note) return null
  return row
}

/**
 * The soil verdict, or null when the reading changes nothing this month. Runs
 * only for a caller that opted in, and only names a task the month's plan
 * contains — the same rule the weather actions follow. Pre-emergent wins over
 * seeding when both windows are open, because pre-emergent blocks seed.
 */
function buildSoilLead(
  soilTempF: number | null,
  grass: { type?: string; confirmed: boolean },
  can: PlanTaskCapabilities,
): WeatherWeekLead | null {
  if (soilTempF === null) return null

  if (can.preEmergent && soilTempF >= PRE_EMERGENT_SOIL_MIN_F && soilTempF <= PRE_EMERGENT_SOIL_MAX_F) {
    return soilTempF < PRE_EMERGENT_SOIL_GO_F
      ? {
          kind: 'soil',
          stance: 'hold',
          headline: `Soil ${soilTempF}°, wait for ${PRE_EMERGENT_SOIL_GO_F}°`,
          action: 'Pre-emergent holds a little longer',
        }
      : {
          kind: 'soil',
          stance: 'go',
          headline: `Soil ${soilTempF}°, pre-emergent time`,
          action: 'Put it down this week',
        }
  }

  if (can.seeding && grass.confirmed) {
    const row = germinationRowFor(grass.type)
    // Fall back to the table's own name so a new germination row still gets
    // a verdict rather than silently losing it.
    const short = row ? (SEED_SHORT_NAMES[row.grass] ?? row.grass.toLowerCase()) : undefined
    if (row && short) {
      const delta = soilTempF - row.minF
      if (delta < 0 && delta >= -SEEDING_SOIL_BAND_F) {
        return {
          kind: 'soil',
          stance: 'hold',
          headline: `Soil ${soilTempF}°, seed at ${row.minF}°`,
          action: `${capitalize(short)} seeding holds until it warms`,
        }
      }
      if (delta >= 0 && delta < SEEDING_SOIL_BAND_F) {
        return {
          kind: 'soil',
          stance: 'go',
          headline: `Soil ${soilTempF}°, ready to seed`,
          action: `Your ${short} seeding task is on`,
        }
      }
    }
  }

  return null
}

function dayIndex(isoDate: string): number | null {
  // Parsed as UTC midnight and read back in UTC so the label never depends on
  // the viewer's timezone. `toLocaleDateString` here would produce a different
  // string on the server than the client and trip React #418 in production.
  const parsed = new Date(`${isoDate}T00:00:00Z`)
  const time = parsed.getTime()
  if (Number.isNaN(time)) return null
  return parsed.getUTCDay()
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function formatInches(value: number): string {
  return value >= 0.1 ? value.toFixed(1) : value.toFixed(2)
}

/**
 * "Wed", "Wed and Thu", or "over 3 days" — kept short enough for the 286px
 * card. Past two days the labels stop being scannable, and naming a count needs
 * the preposition or the headline reads as a fragment ("2.5″ rain 3 days").
 */
function joinDayLabels(days: WeatherWeekDay[]): string {
  if (days.length === 1) return days[0].shortLabel
  if (days.length === 2) return `${days[0].shortLabel} and ${days[1].shortLabel}`
  return `over ${days.length} days`
}

/**
 * First day with no rain that falls AFTER the last wet day, so "water Friday
 * instead" only ever names a day the forecast actually leaves dry.
 */
function firstDryDayAfterRain(days: WeatherWeekDay[]): WeatherWeekDay | null {
  const lastRainIndex = days.reduce((last, day, i) => (day.isRain ? i : last), -1)
  if (lastRainIndex === -1) return null
  return days.slice(lastRainIndex + 1).find((day) => !day.isRain) ?? null
}

function buildLead(
  days: WeatherWeekDay[],
  totals: { totalPrecipIn: number; peakHigh: WeatherWeekDay | null },
  can: PlanTaskCapabilities,
  soil: { tempF: number | null; grassType?: string; grassConfirmed: boolean },
): WeatherWeekLead {
  // Priority cascade mirrors the mobile adapter: frost > heat > rain > dry,
  // with soil slotted after frost. A week can be several of these at once, and
  // the one that changes what you do first wins. Frost outranks soil because a
  // frost night voids the seeding or pre-emergent step soil would have named.
  const frostDay = days.find((day) => day.isFrost)
  if (frostDay) {
    const low = frostDay.lowF
    // Name only the lanes the month actually has. "Seed and feeding hold" on a
    // month whose sole task is overseeding claims a feeding step that isn't there.
    const held = can.seeding && can.feeding ? 'Seeding and feeding' : can.seeding ? 'Seeding' : can.feeding ? 'Feeding' : null
    return {
      kind: 'frost',
      headline: low !== null ? `${low}° low ${frostDay.shortLabel}` : `Frost ${frostDay.shortLabel}`,
      action: held ? `${held} holds until it passes` : null,
    }
  }

  const soilLead = buildSoilLead(soil.tempF, { type: soil.grassType, confirmed: soil.grassConfirmed }, can)
  if (soilLead) return soilLead

  const { peakHigh } = totals
  if (peakHigh && (peakHigh.highF ?? 0) >= HOT_HIGH_F) {
    return {
      kind: 'heat',
      headline: `${peakHigh.highF}° peak ${peakHigh.shortLabel}`,
      action: can.mowing ? 'Your mowing task raises the deck' : null,
    }
  }

  // Measurable rainfall with no "rain day" still has to land here, or a week of
  // light drizzle draws blue bars under the headline "No rain this week".
  const rainDays = days.filter((day) => day.isRain)
  const hasMeasurableRain = totals.totalPrecipIn >= TRACE_PRECIP_IN
  if (rainDays.length > 0 || hasMeasurableRain) {
    const dry = firstDryDayAfterRain(days)
    const inches = formatInches(totals.totalPrecipIn)
    const headline = !hasMeasurableRain
      ? // Chance-driven rain with no forecast amount. "0.00″ rain Mon" reads as
        // a contradiction of its own number.
        `Rain likely ${joinDayLabels(rainDays)}`
      : rainDays.length > 0
        ? `${inches}″ rain ${joinDayLabels(rainDays)}`
        : `${inches}″ light rain this week`

    return {
      kind: 'rain',
      headline,
      action: !can.watering
        ? null
        : dry
          ? `Water ${dry.shortLabel} instead`
          : rainDays.length > 0
            ? 'Skip watering those days'
            : 'Your watering days are already set',
    }
  }

  return {
    kind: 'dry',
    headline: 'No rain this week',
    action: can.watering ? 'Your watering days are already set' : null,
  }
}

/**
 * Reduce the `/api/weather` payload to the strip's view model. Returns null on
 * anything unusable so the caller can fall back rather than render an empty
 * chart.
 */
export function buildWeatherWeek(
  weather: WeatherData | null | undefined,
  taskTexts: string[] = [],
  context: WeatherWeekContext = {},
): WeatherWeek | null {
  const daily = weather?.forecast?.next7days?.daily
  // Require a full, well-formed week. A partial array would render three
  // columns in a seven-column grid while the aria label announces a seven day
  // forecast, and a null row throws on property access. The caller falls back
  // to the checklist rather than showing a broken chart.
  if (!Array.isArray(daily) || daily.length < 7) return null
  const usable = daily.slice(0, 7)
  if (usable.some((day) => !day || typeof day.date !== 'string' || day.date.length === 0)) {
    return null
  }

  const days: WeatherWeekDay[] = usable.map((day, index) => {
    const highF = numberOrNull(day.tempHigh)
    const lowF = numberOrNull(day.tempLow)
    const precipIn = numberOrNull(day.precipAmount) ?? 0
    const precipChance = numberOrNull(day.precipChance) ?? 0
    const weekday = dayIndex(day.date)

    return {
      date: day.date,
      initial: weekday === null ? '·' : DAY_INITIAL[weekday],
      shortLabel: index === 0 ? 'Today' : weekday === null ? '' : DAY_SHORT[weekday],
      longLabel: index === 0 ? 'today' : weekday === null ? '' : DAY_LONG[weekday],
      highF,
      lowF,
      precipIn,
      precipChance,
      barPct:
        precipIn < TRACE_PRECIP_IN
          ? 0
          : Math.max(
              MIN_VISIBLE_BAR_PCT,
              Math.round(Math.min(precipIn / PRECIP_CEILING_IN, 1) * 100),
            ),
      isToday: index === 0,
      isRain: precipChance >= RAIN_CHANCE_PCT || precipIn >= RAIN_AMOUNT_IN,
      isHot: (highF ?? 0) >= HOT_HIGH_F,
      isFrost: lowF !== null && lowF <= FROST_LOW_F,
    }
  })

  const totalPrecipIn = days.reduce((sum, day) => sum + day.precipIn, 0)
  const peakHigh = days.reduce<WeatherWeekDay | null>(
    (peak, day) => (day.highF !== null && (peak === null || day.highF > (peak.highF ?? -Infinity)) ? day : peak),
    null,
  )

  const soilTempF = context.soil ? numberOrNull(weather?.forecast?.next7days?.soilTempF) : null

  return {
    days,
    totalPrecipIn,
    rainDays: days.filter((day) => day.isRain).length,
    peakHighF: peakHigh?.highF ?? null,
    peakHighLabel: peakHigh?.shortLabel ?? null,
    soilTempF,
    lead: buildLead(days, { totalPrecipIn, peakHigh }, planTaskCapabilities(taskTexts), {
      tempF: soilTempF,
      grassType: context.grassType,
      grassConfirmed: context.grassConfirmed === true,
    }),
  }
}
